import {
  Dispatch,
  PropsWithChildren, SetStateAction, createContext, useContext, useMemo,
  useState,
} from 'react';
import {
  Credit, CreditAddress, CreditItem, CreditTypes,
  Insolvency,
} from './Credit';
import { defaultCredit } from './defaultCredit';
import { useCredit } from './useCredit';

export type CreditContextType = {
  id: string,
  credit: Credit,

  securedCredit: Record<string, CreditItem>,
  unsecuredCredit: Record<string, CreditItem>,
  securedCreditAddresses: Record<string, CreditAddress>,
  unsecuredCreditAddresses: Record<string, CreditAddress>,
  insolvencyAddresses: Record<string, CreditAddress>,

  setCreditData: Dispatch<SetStateAction<Credit>>,
  updateCredit(field: string, value: any, disableIncrementUpdateCount?: boolean, initiator?: string): Promise<void>,
  fetchCredit(): Promise<void>,
  appendToCreditArray<T>(field: string, value: T): Promise<void>,
  deleteFromCreditArray(field: string, itemId: string): Promise<void>,

  addCreditItem(value: Partial<CreditItem>, type: CreditTypes, initiator?: string): Promise<void>,
  updateCreditItem(data: Partial<CreditItem>, creditItemId: string, type: CreditTypes, disableIncrementUpdateCount: boolean): Promise<void>,
  deleteCreditItems(creditItem: CreditItem, type: CreditTypes): Promise<void>,
  addInsolvencyItem(data: Partial<Insolvency>, initiator?: string): Promise<void>,
  updateInsolvencyItem(data: Partial<Insolvency>, insolvencyItemId: string, disableIncrementUpdateCount: boolean): Promise<void>,
  deleteInsolvencyItems(insolvencyItem: Insolvency): Promise<void>,
  expandedStates: Record<string, boolean>,
  handleToggleAll(expanded: boolean): void,
  handleToggle(refName: string): void,
}

const defaultCreditContext: CreditContextType = {
  id: '',
  credit: defaultCredit,
  securedCredit: {},
  unsecuredCredit: {},
  securedCreditAddresses: {},
  unsecuredCreditAddresses: {},
  insolvencyAddresses: {},

  setCreditData: () => { },
  updateCredit: async () => { },
  fetchCredit: async () => { },
  appendToCreditArray: async () => { },
  deleteFromCreditArray: async () => { },

  addCreditItem: async () => { },
  updateCreditItem: async () => { },
  deleteCreditItems: async () => { },
  addInsolvencyItem: async () => { },
  updateInsolvencyItem: async () => { },
  deleteInsolvencyItems: async () => { },

  expandedStates: {},
  handleToggleAll: () => { },
  handleToggle: () => { },
};

export const CreditContext = createContext<CreditContextType>(defaultCreditContext);

export function useCreditContext() {
  return useContext(CreditContext);
}

export enum CreditsRefNames {
  SECURED_CREDIT = 'secured-credit-table',
  UNSECURED_CREDIT = 'unsecured-credit-table',
  INSOLVENCY_CREDIT = 'insolvency-credit-table',
}

export function CreditProvider({ id, children }: PropsWithChildren<{ id: string }>) {
  const credit = useCredit(id);

  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(
    Object.values(CreditsRefNames).reduce((acc, refName) => ({
      ...acc,
      [refName]: true,
    }), {}),
  );

  const handleToggleAll = (expanded: boolean) => {
    const sections = Object.values(CreditsRefNames);
    const newStates = [...sections].reduce((acc, refName) => ({
      ...acc,
      [refName]: expanded,
    }), {});
    setExpandedStates(newStates);
  };

  const handleToggle = (refName: string) => {
    setExpandedStates((prev) => ({
      ...prev,
      [refName]: !prev[refName],
    }));
  };

  const contextValue = useMemo(() => ({
    ...credit,
    expandedStates,
    handleToggleAll,
    handleToggle,
    id,
  }), [id, credit]);

  return (
    <CreditContext.Provider value={contextValue}>{children}</CreditContext.Provider>
  );
}
